// ============================================================================
// SourceFilesTab — MS-13
// ----------------------------------------------------------------------------
// Tab "Arquivos" em /sources. Permite:
//   • Upload de PDF/TXT/MD (até 10 MB) com escopo (privado-do-grupo / global).
//   • Listagem dos documentos da org com status de ingestão.
//   • Re-ingestão e exclusão.
//
// Fluxo de upload:
//   1. Cria registro em source_documents (status=pending).
//   2. Sobe arquivo para storage 'source-files' em <org_id>/<doc_id>-<filename>.
//   3. Invoca edge function source-ingest com document_id.
//   4. Polling a cada 3s atualiza status.
// ============================================================================

import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentOrgId } from "@/hooks/useSkillOS";
import { groupsApi } from "@/services/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import { useToast } from "@/hooks/use-toast";
import { Upload, FileText, RefreshCw, Loader2, Sparkles, AlertCircle, CheckCircle2, Clock } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Progress } from "@/components/ui/progress";

const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPTED = ".pdf,.txt,.md,.docx";

type Scope = "group" | "global";
type FileType = "pdf" | "txt" | "md" | "docx";
interface SourceDoc {
  id: string;
  filename: string;
  file_type: FileType;
  size_bytes: number;
  status: "pending" | "processing" | "ready" | "failed";
  error_message: string | null;
  scope: Scope;
  owner_group_id: string | null;
  chunk_count: number;
  ingested_at: string | null;
  created_at: string;
  summary: string | null;
  tags: string[];
  summarized_at: string | null;
}

const STATUS_VARIANT: Record<SourceDoc["status"], "default" | "secondary" | "destructive" | "outline"> = {
  pending: "outline",
  processing: "secondary",
  ready: "default",
  failed: "destructive",
};

type UploadStage = "idle" | "creating" | "uploading" | "ingesting" | "done" | "error";
interface UploadLogEntry {
  ts: number;
  level: "info" | "success" | "error";
  message: string;
}

function stageProgress(stage: UploadStage): number {
  switch (stage) {
    case "creating": return 15;
    case "uploading": return 55;
    case "ingesting": return 85;
    case "done": return 100;
    case "error": return 100;
    default: return 0;
  }
}

function stageLabel(stage: UploadStage): string {
  switch (stage) {
    case "creating": return "Criando registro do documento…";
    case "uploading": return "Enviando arquivo para o storage…";
    case "ingesting": return "Disparando pipeline de ingestão…";
    case "done": return "Upload concluído. Ingestão em background.";
    case "error": return "Falha no upload.";
    default: return "";
  }
}

function elapsedFrom(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}min`;
  return `${Math.floor(ms / 3_600_000)}h`;
}

export default function SourceFilesTab() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data: orgId } = useCurrentOrgId();
  const fileInput = useRef<HTMLInputElement>(null);
  const [scope, setScope] = useState<Scope>("group");
  const [groupId, setGroupId] = useState<string>("");
  const [uploading, setUploading] = useState(false);
  const [uploadStage, setUploadStage] = useState<UploadStage>("idle");
  const [uploadLog, setUploadLog] = useState<UploadLogEntry[]>([]);
  const [currentFileName, setCurrentFileName] = useState<string>("");

  const pushLog = (level: UploadLogEntry["level"], message: string) =>
    setUploadLog((prev) => [...prev, { ts: Date.now(), level, message }].slice(-20));

  const resetUploadState = () => {
    setUploadStage("idle");
    setUploadLog([]);
    setCurrentFileName("");
  };

  const { data: groups = [] } = useQuery({ queryKey: ["groups"], queryFn: groupsApi.list });

  const { data: docs = [], isLoading } = useQuery<SourceDoc[]>({
    queryKey: ["source-documents", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("source_documents")
        .select("id,filename,file_type,size_bytes,status,error_message,scope,owner_group_id,chunk_count,ingested_at,created_at,summary,tags,summarized_at")
        .order("created_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as SourceDoc[];
    },
    refetchInterval: (q) => {
      const list = (q.state.data ?? []) as SourceDoc[];
      return list.some((d) => d.status === "pending" || d.status === "processing") ? 3000 : false;
    },
  });

  const uploadMut = useMutation({
    mutationFn: async (file: File) => {
      if (!orgId) throw new Error("org_id ausente");
      if (file.size > MAX_BYTES) throw new Error("Arquivo maior que 10 MB");
      const ext = file.name.split(".").pop()?.toLowerCase();
      const fileType: FileType =
        ext === "pdf" ? "pdf" :
        ext === "docx" ? "docx" :
        ext === "md" ? "md" :
        ext === "txt" ? "txt" : "txt";
      if (!["pdf", "txt", "md", "docx"].includes(fileType)) throw new Error("Tipo não suportado");
      if (scope === "group" && !groupId) throw new Error("Selecione um grupo");

      setCurrentFileName(file.name);
      setUploadLog([]);
      pushLog("info", `Iniciando upload de "${file.name}" (${(file.size / 1024).toFixed(1)} KB, ${fileType.toUpperCase()})`);

      // 1) cria registro
      setUploadStage("creating");
      pushLog("info", "Criando registro em source_documents…");
      const { data: doc, error: insErr } = await supabase
        .from("source_documents")
        .insert({
          org_id: orgId,
          filename: file.name,
          file_type: fileType,
          size_bytes: file.size,
          scope,
          owner_group_id: scope === "group" ? groupId : null,
          storage_path: `${orgId}/_pending_${Date.now()}_${file.name}`,
        })
        .select("id")
        .single();
      if (insErr || !doc) {
        pushLog("error", `Falha ao criar registro: ${insErr?.message ?? "desconhecido"}`);
        throw new Error(insErr?.message ?? "Falha ao criar documento");
      }
      pushLog("success", `Registro criado (id=${doc.id.slice(0, 8)}…)`);

      // 2) upload
      setUploadStage("uploading");
      pushLog("info", "Subindo arquivo para bucket source-files…");
      const path = `${orgId}/${doc.id}-${file.name}`;
      const { error: upErr } = await supabase.storage
        .from("source-files")
        .upload(path, file, { contentType: file.type || "application/octet-stream", upsert: false });
      if (upErr) {
        pushLog("error", `Falha no storage: ${upErr.message}`);
        await supabase.from("source_documents").delete().eq("id", doc.id);
        throw new Error(upErr.message);
      }
      pushLog("success", `Arquivo enviado para ${path}`);
      await supabase.from("source_documents").update({ storage_path: path }).eq("id", doc.id);

      // 3) ingest
      setUploadStage("ingesting");
      pushLog("info", "Invocando edge function source-ingest…");
      const { error: invErr } = await supabase.functions.invoke("source-ingest", {
        body: { document_id: doc.id },
      });
      if (invErr) {
        pushLog("error", `Falha ao invocar ingestão: ${invErr.message}`);
        throw new Error(invErr.message);
      }
      pushLog("success", "Pipeline de ingestão iniciada — extração + chunking + embeddings rodando em background.");
      setUploadStage("done");
      return doc.id;
    },
    onSuccess: () => {
      toast({ title: "Arquivo enviado", description: "Ingestão iniciada." });
      qc.invalidateQueries({ queryKey: ["source-documents", orgId] });
    },
    onError: (e: Error) => {
      setUploadStage("error");
      toast({ title: "Falha no upload", description: e.message, variant: "destructive" });
    },
    onSettled: () => setUploading(false),
  });

  const reingestMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.functions.invoke("source-ingest", { body: { document_id: id } });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      toast({ title: "Reprocessando" });
      qc.invalidateQueries({ queryKey: ["source-documents", orgId] });
    },
    onError: (e: Error) => toast({ title: "Falha", description: e.message, variant: "destructive" }),
  });

  const summarizeMut = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase.functions.invoke("source-summarize", { body: { document_id: id } });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      toast({ title: "Resumo gerado", description: "Resumo e tags atualizados." });
      qc.invalidateQueries({ queryKey: ["source-documents", orgId] });
    },
    onError: (e: Error) => toast({ title: "Falha ao resumir", description: e.message, variant: "destructive" }),
  });

  const deleteMut = useMutation({
    mutationFn: async (doc: SourceDoc & { storage_path?: string }) => {
      const { data: full } = await supabase
        .from("source_documents").select("storage_path").eq("id", doc.id).maybeSingle();
      if (full?.storage_path) {
        await supabase.storage.from("source-files").remove([full.storage_path]);
      }
      const { error } = await supabase.from("source_documents").delete().eq("id", doc.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Arquivo removido" });
      qc.invalidateQueries({ queryKey: ["source-documents", orgId] });
    },
  });

  useEffect(() => {
    if (scope === "global") setGroupId("");
  }, [scope]);

  const onPick = (f: FileList | null) => {
    if (!f || !f[0]) return;
    setUploading(true);
    uploadMut.mutate(f[0]);
    if (fileInput.current) fileInput.current.value = "";
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Upload className="w-4 h-4" /> Enviar arquivo (PDF, Word, TXT, MD — até 10 MB)
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Escopo</label>
              <Select value={scope} onValueChange={(v) => setScope(v as Scope)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="group">Privado do grupo</SelectItem>
                  <SelectItem value="global">Global da org</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {scope === "group" && (
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Grupo dono</label>
                <Select value={groupId} onValueChange={setGroupId}>
                  <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Arquivo</label>
              <Input
                ref={fileInput}
                type="file"
                accept={ACCEPTED}
                disabled={uploading || !orgId || (scope === "group" && !groupId)}
                onChange={(e) => onPick(e.target.files)}
              />
            </div>
          </div>
          {(uploading || uploadStage !== "idle") && (
            <div className="space-y-2 rounded-md border bg-muted/30 p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-xs font-medium text-foreground min-w-0">
                  {uploadStage === "done" && <CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0" />}
                  {uploadStage === "error" && <AlertCircle className="w-3.5 h-3.5 text-destructive shrink-0" />}
                  {(uploading && uploadStage !== "done" && uploadStage !== "error") && (
                    <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
                  )}
                  <span className="truncate">{currentFileName || "Upload"}</span>
                </div>
                <span className="text-[10px] text-muted-foreground shrink-0">{stageLabel(uploadStage)}</span>
              </div>
              <Progress value={stageProgress(uploadStage)} className="h-1.5" />
              {uploadLog.length > 0 && (
                <div className="max-h-32 overflow-y-auto space-y-0.5 font-mono text-[10px] leading-tight">
                  {uploadLog.map((entry, i) => (
                    <div
                      key={i}
                      className={
                        entry.level === "error" ? "text-destructive"
                        : entry.level === "success" ? "text-primary"
                        : "text-muted-foreground"
                      }
                    >
                      <span className="opacity-60">[{new Date(entry.ts).toLocaleTimeString("pt-BR")}]</span>{" "}
                      {entry.message}
                    </div>
                  ))}
                </div>
              )}
              {(uploadStage === "done" || uploadStage === "error") && (
                <Button variant="ghost" size="sm" className="h-6 text-[10px]" onClick={resetUploadState}>
                  Limpar log
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Arquivo</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Escopo</TableHead>
                <TableHead>Chunks</TableHead>
                <TableHead>Resumo / Tags</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Enviado</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">Carregando…</TableCell></TableRow>
              )}
              {!isLoading && docs.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">Nenhum arquivo enviado</TableCell></TableRow>
              )}
              {docs.map((d) => (
                <TableRow key={d.id}>
                  <TableCell className="font-medium flex items-center gap-2">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    <span className="truncate max-w-[260px]" title={d.filename}>{d.filename}</span>
                  </TableCell>
                  <TableCell className="uppercase text-xs">{d.file_type}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {d.scope === "group" ? "Grupo" : "Global"}
                  </TableCell>
                  <TableCell className="tabular-nums">{d.chunk_count}</TableCell>
                  <TableCell className="max-w-[260px]">
                    {d.summary ? (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button type="button" className="text-left text-xs text-foreground hover:underline line-clamp-2">
                            {d.summary}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-96 text-xs space-y-2">
                          <p className="whitespace-pre-wrap">{d.summary}</p>
                          {d.tags?.length > 0 && (
                            <div className="flex flex-wrap gap-1 pt-1 border-t">
                              {d.tags.map((t) => (
                                <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>
                              ))}
                            </div>
                          )}
                          {d.summarized_at && (
                            <p className="text-[10px] text-muted-foreground">
                              Gerado em {new Date(d.summarized_at).toLocaleString("pt-BR")}
                            </p>
                          )}
                        </PopoverContent>
                      </Popover>
                    ) : (
                      <span className="text-xs text-muted-foreground italic">— sem resumo —</span>
                    )}
                    {d.tags?.length > 0 && !d.summary && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {d.tags.slice(0, 3).map((t) => (
                          <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>
                        ))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-0.5">
                      <Badge variant={STATUS_VARIANT[d.status]} className="text-[10px] uppercase w-fit">
                        {d.status === "processing" && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
                        {d.status === "ready" && <CheckCircle2 className="w-3 h-3 mr-1" />}
                        {d.status === "failed" && <AlertCircle className="w-3 h-3 mr-1" />}
                        {d.status === "pending" && <Clock className="w-3 h-3 mr-1" />}
                        {d.status}
                      </Badge>
                      {(d.status === "pending" || d.status === "processing") && (
                        <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                          <Clock className="w-2.5 h-2.5" /> há {elapsedFrom(d.created_at)}
                        </span>
                      )}
                      {d.status === "ready" && d.ingested_at && (
                        <span className="text-[10px] text-muted-foreground">
                          em {elapsedFrom(d.created_at)}
                        </span>
                      )}
                      {d.status === "failed" && d.error_message && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button type="button" className="text-[10px] text-destructive hover:underline text-left truncate max-w-[180px]">
                              {d.error_message}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-80 text-xs">
                            <p className="font-medium text-destructive mb-1 flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" /> Erro de ingestão
                            </p>
                            <p className="whitespace-pre-wrap font-mono text-[10px] text-foreground">
                              {d.error_message}
                            </p>
                            <p className="text-[10px] text-muted-foreground mt-2 pt-2 border-t">
                              Use "Reprocessar" para tentar novamente.
                            </p>
                          </PopoverContent>
                        </Popover>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(d.created_at).toLocaleString("pt-BR")}
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button
                      variant="ghost" size="icon"
                      onClick={() => summarizeMut.mutate(d.id)}
                      disabled={summarizeMut.isPending || d.status !== "ready"}
                      title={d.summary ? "Regerar resumo e tags" : "Gerar resumo e tags"}
                    >
                      {summarizeMut.isPending && summarizeMut.variables === d.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Sparkles className={`w-4 h-4 ${d.summary ? "text-primary" : ""}`} />
                      )}
                    </Button>
                    <Button
                      variant="ghost" size="icon"
                      onClick={() => reingestMut.mutate(d.id)}
                      disabled={reingestMut.isPending || d.status === "processing"}
                      title="Reprocessar"
                    >
                      <RefreshCw className={`w-4 h-4 ${d.status === "processing" ? "animate-spin" : ""}`} />
                    </Button>
                    <ConfirmDeleteDialog
                      itemName={d.filename}
                      itemType="arquivo"
                      onConfirm={() => deleteMut.mutate(d)}
                      disabled={deleteMut.isPending}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
